import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { SectionHeader } from '../components/PageHeader';

interface ErrorEvent {
  id: string;
  occurredAt: string;
  source: 'api' | 'worker';
  requestId: string | null;
  method: string | null;
  route: string | null;
  status: number | null;
  code: string | null;
  message: string;
  stack: string | null;
  context: Record<string, unknown>;
  tenantId: string | null;
  tenantName: string | null;
  userId: string | null;
  userEmail: string | null;
}

interface Page { errors: ErrorEvent[]; nextBefore: string | null }

/**
 * The platform error log. Operator-only, because a message can name another tenant's org or people.
 * It exists so a failure can be diagnosed from the browser rather than by shelling into the droplet.
 */
export function Errors() {
  const [errors, setErrors] = useState<ErrorEvent[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState<'' | 'api' | 'worker'>('');
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (before?: string | null) => {
    setBusy(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (before) params.set('before', before);
      if (search) params.set('q', search);
      if (source) params.set('source', source);
      const page = await api<Page>(`/api/admin/errors?${params}`);
      setErrors((prev) => (before ? [...prev, ...page.errors] : page.errors));
      setNextBefore(page.nextBefore);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the error log');
    } finally {
      setBusy(false);
    }
  }, [search, source]);

  useEffect(() => { void load(); }, [load]);

  return (
    <>
      <SectionHeader
        title="Errors"
        blurb="Server-side failures, newest first. Quote the request id to tie one to a report from a user."
      />
      {error && <div className="alert">{error}</div>}

      <div className="card">
        <form className="row" onSubmit={(e) => { e.preventDefault(); void load(); }}>
          <input placeholder="Search message, route or request id" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select value={source} onChange={(e) => setSource(e.target.value as '' | 'api' | 'worker')}>
            <option value="">everywhere</option>
            <option value="api">api</option>
            <option value="worker">worker</option>
          </select>
          <button type="submit" disabled={busy}>Search</button>
        </form>
      </div>

      <div className="card flush">
        <table>
          <thead><tr><th>When</th><th>Where</th><th>What</th><th>Who</th><th className="actions-cell"></th></tr></thead>
          <tbody>
            {errors.length === 0 && !busy && <tr><td colSpan={5} className="empty">Nothing logged.</td></tr>}
            {errors.map((e) => (
              <tr key={e.id}>
                <td className="small">{new Date(e.occurredAt).toLocaleString()}</td>
                <td className="small">
                  <span className="pill">{e.source}</span>{' '}
                  <span className="mono">{e.method ? `${e.method} ` : ''}{e.route ?? ''}</span>
                </td>
                <td>{e.message}{e.code && <><br /><span className="small muted mono">{e.code}</span></>}</td>
                <td className="small muted">{e.userEmail ?? '—'}{e.tenantName && <><br />{e.tenantName}</>}</td>
                <td className="actions-cell">
                  <button className="secondary" onClick={() => setOpen(open === e.id ? null : e.id)}>{open === e.id ? 'Hide' : 'Detail'}</button>
                </td>
              </tr>
            ))}
            {errors.filter((e) => e.id === open).map((e) => (
              <tr key={`${e.id}-detail`}>
                <td colSpan={5}>
                  <p className="small muted">request id <span className="mono">{e.requestId ?? 'none'}</span></p>
                  {Object.keys(e.context).length > 0 && <pre className="small">{JSON.stringify(e.context, null, 2)}</pre>}
                  <pre className="small">{e.stack ?? 'No stack recorded.'}</pre>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {nextBefore && (
        <div className="actions" style={{ marginTop: 16 }}>
          <button className="secondary" onClick={() => void load(nextBefore)} disabled={busy}>Load older</button>
        </div>
      )}
    </>
  );
}
